import { useAuth0 } from '@auth0/auth0-react';
import {
  InfiniteData,
  QueryClient,
  useMutation,
  useQuery,
  useQueryClient
} from '@tanstack/react-query';
import { toast } from 'sonner';

import { PaginatedConversationsResponse } from '@/app/(authed-react-query)/(authed-sidebars)/chats/types';

import {
  generateConversationLabel,
  getConversationMetadata,
  getReplayConversation,
  patchConversationName
} from './api';
import type { ConversationMetadata, ReplayConversation } from './types';

type ChatHistoryData = InfiniteData<PaginatedConversationsResponse>;

// ---------------------------------------------------------------------------
// Query Keys
// ---------------------------------------------------------------------------
export const conversationKeys = {
  all: ['conversation'] as const,
  detail: (conversationId: string) => [...conversationKeys.all, conversationId] as const,
  replay: (conversationId: string) => ['conversation-replay', conversationId] as const
};

export function useReplayConversation(conversationId: string | null | undefined) {
  return useQuery<ReplayConversation | null>({
    queryKey: conversationKeys.replay(conversationId ?? ''),
    queryFn: () => getReplayConversation(conversationId),
    enabled: !!conversationId,
    gcTime: 0,
    retry: false
  });
}

// ---------------------------------------------------------------------------
// Cache write helper — the ONLY place conversation metadata gets written.
// Merges a patch into the canonical detail cache and mirrors it into the
// ['chat-history'] list rows so every consumer converges immediately.
// ---------------------------------------------------------------------------
export function patchConversationCaches(
  queryClient: QueryClient,
  conversationId: string,
  patch: Partial<ConversationMetadata>
) {
  queryClient.setQueryData<ConversationMetadata>(
    conversationKeys.detail(conversationId),
    (current) => (current ? { ...current, ...patch } : current)
  );

  queryClient.setQueriesData<ChatHistoryData>({ queryKey: ['chat-history'] }, (data) => {
    if (!data) return data;
    return {
      ...data,
      pages: data.pages.map((page) =>
        page.conversations?.[conversationId]
          ? {
              ...page,
              conversations: {
                ...page.conversations,
                [conversationId]: {
                  ...page.conversations[conversationId],
                  ...patch
                }
              }
            }
          : page
      )
    };
  });
}

function findConversationInHistory(
  queryClient: QueryClient,
  conversationId: string | null | undefined
): ConversationMetadata | undefined {
  if (!conversationId) return undefined;

  const entries = queryClient.getQueriesData<ChatHistoryData>({ queryKey: ['chat-history'] });
  for (const [, data] of entries) {
    const row = data?.pages.find((page) => page.conversations?.[conversationId])?.conversations?.[
      conversationId
    ];
    if (row) {
      return {
        id: conversationId,
        name: row.name ?? null,
        updated_at: row.updated_at ?? null,
        is_public: row.is_public ?? null,
        deleted_at: null,
        user_id: row.user_id ?? null,
        model: row.model ?? null,
        chat_type: row.chat_type ?? null,
        tool_preferences: (row.tool_preferences as Record<string, unknown>) ?? null
      };
    }
  }

  return undefined;
}

// ---------------------------------------------------------------------------
// useConversationMetadata — the single source of truth for conversation
// metadata (name, chat_type, is_public, ...). Seeded from the chat-history
// cache while the fetch is in flight so titles never flash.
// ---------------------------------------------------------------------------
export function useConversationMetadata(conversationId: string | null | undefined) {
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();
  const queryClient = useQueryClient();

  return useQuery<ConversationMetadata>({
    queryKey: conversationKeys.detail(conversationId ?? ''),
    queryFn: async () => {
      if (!conversationId) throw new Error('No conversation ID');
      const accessToken = isAuthenticated ? await getAccessTokenSilently() : '';
      return getConversationMetadata(conversationId, accessToken);
    },
    enabled: !!conversationId,
    staleTime: 30_000,
    placeholderData: () => findConversationInHistory(queryClient, conversationId)
  });
}

// ---------------------------------------------------------------------------
// useRenameConversation — optimistic rename across detail + list caches.
// ---------------------------------------------------------------------------
export function useRenameConversation() {
  const { getAccessTokenSilently } = useAuth0();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      conversationId,
      name
    }: {
      conversationId: string;
      name: string | null;
    }) => {
      const accessToken = await getAccessTokenSilently();
      await patchConversationName(conversationId, name, accessToken);
    },
    onMutate: async ({ conversationId, name }) => {
      await Promise.all([
        queryClient.cancelQueries({ queryKey: ['chat-history'] }),
        queryClient.cancelQueries({ queryKey: conversationKeys.detail(conversationId) })
      ]);

      const previousDetail = queryClient.getQueryData<ConversationMetadata>(
        conversationKeys.detail(conversationId)
      );
      const previousHistory = queryClient.getQueriesData<ChatHistoryData>({
        queryKey: ['chat-history']
      });

      patchConversationCaches(queryClient, conversationId, { name });

      return { previousDetail, previousHistory };
    },
    onError: (_error, { conversationId }, context) => {
      if (context?.previousDetail) {
        queryClient.setQueryData(conversationKeys.detail(conversationId), context.previousDetail);
      }
      context?.previousHistory?.forEach(([key, data]) => queryClient.setQueryData(key, data));
      toast.error('Failed to rename conversation');
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['chat-history'] });
      void queryClient.invalidateQueries({ queryKey: ['chat-search'] });
    }
  });
}

// ---------------------------------------------------------------------------
// useGenerateConversationTitle — LLM title generation for unnamed
// conversations. Two-step on the wire (label, then persist via PATCH);
// aborts if a name shows up mid-flight (e.g. the user renamed manually).
// ---------------------------------------------------------------------------
export function useGenerateConversationTitle() {
  const { getAccessTokenSilently } = useAuth0();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (conversationId: string) => {
      const accessToken = await getAccessTokenSilently();
      const { label } = await generateConversationLabel(conversationId, accessToken);
      if (!label) return null;

      const current = queryClient.getQueryData<ConversationMetadata>(
        conversationKeys.detail(conversationId)
      );
      if (current?.name) return null;

      await patchConversationName(conversationId, label, accessToken);
      return label;
    },
    onSuccess: (label, conversationId) => {
      if (!label) return;
      patchConversationCaches(queryClient, conversationId, { name: label });
      void queryClient.invalidateQueries({ queryKey: ['chat-history'] });
    }
  });
}
